<template>
    <div>
    <v-card class="mx-auto mt-4" max-width="98%" flat color="transparent">
    <v-toolbar color="#37474F" dark rounded="lg" class="mb-6 elevation-4 flex-wrap" style="height: auto;">
        <v-toolbar-title class="font-weight-bold">
            <v-icon left>mdi-chat-processing-outline</v-icon>
            채팅 관리
        </v-toolbar-title>
        <v-spacer/>

        <v-btn-toggle
            v-model="viewMode"
            mandatory
            class="mr-4 elevation-2 flex-wrap"
            dense
            @change="OnChangeViewMode"
        >
            <v-btn value="chat" dark>
                <v-icon left small>mdi-message-text</v-icon>
                채팅 내역
            </v-btn>
            <v-btn value="report" dark>
                <v-icon left small>mdi-alert-octagon</v-icon>
                신고 내역
            </v-btn>
        </v-btn-toggle>

        <v-divider vertical class="mx-2" inset></v-divider>

        <v-col cols="12" sm="3" md="2" class="pa-0 ml-2">
            <v-text-field
                v-model="search_user_id"
                label="유저 ID 검색"
                hide-details
                dense
                outlined
                dark
                clearable
                @keydown.enter="OnSearch"
            ></v-text-field>
        </v-col>
        <v-btn icon dark @click="OnSearch" class="ml-1">
            <v-icon>mdi-magnify</v-icon>
        </v-btn>
        <v-btn icon dark @click="LoadList" class="ml-1">
            <v-icon>mdi-refresh</v-icon>
        </v-btn>
    </v-toolbar>

    <div v-if="loadingData" class="text-center py-10">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>

    <!-- 채팅 내역 테이블 -->
    <v-card v-else-if="viewMode === 'chat'" class="elevation-2" style="overflow-x: auto;">
        <v-data-table
            :headers="headers_chat"
            :items="chat_list"
            :items-per-page="-1"
            :footer-props="{'items-per-page-options':[50, 100, -1]}"
            class="elevation-0"
        >
            <template v-slot:item="{ item }">
                <tr>
                    <td>{{ item.PID }}</td>
                    <td>
                        <div class="d-flex align-center">
                            <v-avatar size="28" class="mr-2" v-if="item.PROFILE_IMG">
                                <img :src="item.PROFILE_IMG" />
                            </v-avatar>
                            <div>
                                <div class="font-weight-medium">{{ item.NICK_NAME || '-' }}</div>
                                <div class="text-caption grey--text">{{ item.USER_ID }}</div>
                            </div>
                        </div>
                    </td>
                    <td style="max-width: 400px;">
                        <div class="text-truncate" v-html="ParseColorMessage(item.MESSAGE)"></div>
                    </td>
                    <td>
                        <v-chip x-small :color="item.MSG_TYPE === 'TEXT' ? 'grey' : 'blue'" dark>
                            {{ item.MSG_TYPE || 'TEXT' }}
                        </v-chip>
                    </td>
                    <td>
                        <v-chip x-small :color="GetBanColor(item)" dark>
                            {{ GetBanText(item) }}
                        </v-chip>
                    </td>
                    <td>
                        <span v-if="item.REPORT_TOTAL > 0" class="red--text font-weight-bold">{{ item.REPORT_TOTAL }}</span>
                        <span v-else class="grey--text">0</span>
                    </td>
                    <td>{{ FormatDate(item.CREATE_DT) }}</td>
                    <td>
                        <v-btn icon small @click="OpenUserDialog(item.USER_ID)" title="유저 정보">
                            <v-icon small>mdi-account-search</v-icon>
                        </v-btn>
                        <v-btn icon small @click="SearchByUser(item.USER_ID)" title="이 유저 채팅만 보기">
                            <v-icon small>mdi-filter-variant</v-icon>
                        </v-btn>
                    </td>
                </tr>
            </template>
        </v-data-table>
    </v-card>

    <!-- 신고 내역 테이블 -->
    <v-card v-else-if="viewMode === 'report'" class="elevation-2" style="overflow-x: auto;">
        <v-data-table
            :headers="headers_report"
            :items="report_list"
            :items-per-page="-1"
            :footer-props="{'items-per-page-options':[50, 100, -1]}"
            class="elevation-0"
        >
            <template v-slot:item="{ item }">
                <tr>
                    <td>{{ item.PID }}</td>
                    <td>
                        <div>
                            <div class="font-weight-medium">{{ item.REPORTED_NICK_NAME || '-' }}</div>
                            <div class="text-caption grey--text">{{ item.USER_ID }}</div>
                        </div>
                    </td>
                    <td>{{ item.REPORT_USER_ID }}</td>
                    <td>
                        <v-chip x-small color="orange" dark>{{ item.REPORT_TYPE || '-' }}</v-chip>
                    </td>
                    <td style="max-width: 300px;">
                        <div class="text-truncate">{{ item.CHAT_MESSAGE || '-' }}</div>
                    </td>
                    <td>{{ FormatDate(item.CREATE_DT) }}</td>
                    <td>
                        <v-btn icon small @click="OpenUserDialog(item.USER_ID)" title="유저 정보">
                            <v-icon small>mdi-account-search</v-icon>
                        </v-btn>
                        <v-btn icon small color="red" @click="OpenBanDialog(item.USER_ID)" title="정지">
                            <v-icon small>mdi-cancel</v-icon>
                        </v-btn>
                    </td>
                </tr>
            </template>
        </v-data-table>
    </v-card>

    <v-row v-else class="fill-height py-16" align="center" justify="center">
        <v-col cols="12" class="text-center">
            <v-icon size="100" color="grey lighten-1">mdi-chat-remove-outline</v-icon>
            <div class="text-h6 grey--text mt-4">데이터가 없습니다.</div>
        </v-col>
    </v-row>
    </v-card>

    <!-- 유저 정보 다이얼로그 -->
    <v-dialog v-model="userDialog" max-width="600" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card v-if="userInfo">
            <v-card-title class="blue-grey darken-4 white--text">
                <v-icon left dark>mdi-account</v-icon>
                채팅 유저 정보
                <v-spacer/>
                <v-btn icon dark @click="userDialog = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-card-text class="pt-4">
                <v-simple-table>
                    <tbody>
                        <tr><td class="font-weight-bold" width="140">유저 ID</td><td>{{ userInfo.USER_ID }}</td></tr>
                        <tr><td class="font-weight-bold">닉네임</td><td>{{ userInfo.NICK_NAME || '-' }}</td></tr>
                        <tr><td class="font-weight-bold">누적 신고</td><td class="red--text font-weight-bold">{{ userInfo.REPORT_TOTAL || 0 }}</td></tr>
                        <tr><td class="font-weight-bold">최근 신고</td><td>{{ userInfo.REPORT_RECENT || 0 }}</td></tr>
                        <tr>
                            <td class="font-weight-bold">정지 상태</td>
                            <td>
                                <v-chip small :color="IsBanned(userInfo) ? 'red' : 'green'" dark>
                                    {{ IsBanned(userInfo) ? '정지중 (' + FormatDate(userInfo.BAN_TIME) + ' 까지)' : '정상' }}
                                </v-chip>
                            </td>
                        </tr>
                        <tr><td class="font-weight-bold">등록일</td><td>{{ FormatDate(userInfo.CREATE_DT) }}</td></tr>
                    </tbody>
                </v-simple-table>
            </v-card-text>
            <v-divider/>
            <v-card-actions class="pa-4">
                <v-btn v-if="!IsBanned(userInfo)" color="red" dark @click="OpenBanDialog(userInfo.USER_ID)">
                    <v-icon left small>mdi-cancel</v-icon> 채팅 정지
                </v-btn>
                <v-btn v-else color="green" dark @click="UnbanUser(userInfo.USER_ID)">
                    <v-icon left small>mdi-check-circle</v-icon> 정지 해제
                </v-btn>
                <v-spacer/>
                <v-btn text @click="userDialog = false">닫기</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- 정지 다이얼로그 -->
    <v-dialog v-model="banDialog" max-width="400" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card>
            <v-card-title class="red white--text">
                <v-icon left dark>mdi-cancel</v-icon>
                채팅 정지
            </v-card-title>
            <v-card-text class="pt-6">
                <div class="mb-4 text-subtitle-1">
                    <b>{{ banTargetUserId }}</b> 유저를 채팅 정지합니다.
                </div>
                <v-select
                    v-model="banHours"
                    :items="banOptions"
                    item-text="text"
                    item-value="value"
                    label="정지 기간"
                    outlined
                    dense
                    hide-details
                ></v-select>
            </v-card-text>
            <v-divider/>
            <v-card-actions class="pa-4">
                <v-spacer/>
                <v-btn text @click="banDialog = false">취소</v-btn>
                <v-btn color="red" dark @click="BanUser">정지 처리</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top>
        {{ snackbarText }}
    </v-snackbar>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';

export default {
    name: 'Game_ChatManage',

    data: () => ({
        loadingData: false,
        viewMode: 'chat',
        search_user_id: '',
        chat_list: [],
        report_list: [],

        headers_chat: [
            { text: 'PID', value: 'PID', width: '70' },
            { text: '유저', value: 'USER_ID', width: '180' },
            { text: '메시지', value: 'MESSAGE' },
            { text: '타입', value: 'MSG_TYPE', width: '80' },
            { text: '밴 상태', value: 'IS_BAN_CHAT', width: '90' },
            { text: '신고', value: 'REPORT_TOTAL', width: '70' },
            { text: '작성일', value: 'CREATE_DT', width: '150' },
            { text: '액션', value: 'actions', width: '100', sortable: false },
        ],
        headers_report: [
            { text: 'PID', value: 'PID', width: '70' },
            { text: '신고 대상', value: 'USER_ID', width: '180' },
            { text: '신고자', value: 'REPORT_USER_ID', width: '140' },
            { text: '신고 유형', value: 'REPORT_TYPE', width: '100' },
            { text: '채팅 내용', value: 'CHAT_MESSAGE' },
            { text: '신고일', value: 'CREATE_DT', width: '150' },
            { text: '액션', value: 'actions', width: '100', sortable: false },
        ],

        // 유저 정보 다이얼로그
        userDialog: false,
        userInfo: null,

        // 정지 다이얼로그
        banDialog: false,
        banTargetUserId: '',
        banHours: 24,
        banOptions: [
            { text: '1시간', value: 1 },
            { text: '6시간', value: 6 },
            { text: '12시간', value: 12 },
            { text: '24시간 (1일)', value: 24 },
            { text: '72시간 (3일)', value: 72 },
            { text: '168시간 (7일)', value: 168 },
            { text: '720시간 (30일)', value: 720 },
        ],

        snackbar: false,
        snackbarText: '',
        snackbarColor: 'success',
    }),

    created() {
        this.LoadList();
    },

    methods: {
        ...mapActions([
            'CARRYGOLD_GET_CHAT_LIST',
            'CARRYGOLD_GET_CHAT_LIST_BY_USER',
            'CARRYGOLD_GET_CHAT_REPORT_LIST',
            'CARRYGOLD_GET_CHAT_USER_INFO',
            'CARRYGOLD_BAN_CHAT_USER',
            'CARRYGOLD_UNBAN_CHAT_USER',
        ]),

        async LoadList() {
            this.loadingData = true;
            try {
                if (this.viewMode === 'chat') {
                    const userId = (this.search_user_id || '').trim();
                    let body_data;
                    if (userId) {
                        body_data = await this.CARRYGOLD_GET_CHAT_LIST_BY_USER({ user_id: userId, count: 200 });
                        this.chat_list = body_data.chat_list || [];
                    } else {
                        body_data = await this.CARRYGOLD_GET_CHAT_LIST({ count: 200 });
                        this.chat_list = body_data.chat_list || [];
                    }
                } else {
                    const userId = (this.search_user_id || '').trim();
                    const body_data = await this.CARRYGOLD_GET_CHAT_REPORT_LIST({ user_id: userId || undefined, count: 200 });
                    this.report_list = body_data.report_list || [];
                }
            } catch (err) {
                console.error(err);
                this.ShowSnackbar('데이터를 불러오는데 실패했습니다.', 'error');
            } finally {
                this.loadingData = false;
            }
        },

        OnChangeViewMode() {
            this.LoadList();
        },

        OnSearch() {
            this.LoadList();
        },

        SearchByUser(user_id) {
            this.search_user_id = user_id;
            this.viewMode = 'chat';
            this.LoadList();
        },

        async OpenUserDialog(user_id) {
            try {
                const body_data = await this.CARRYGOLD_GET_CHAT_USER_INFO({ user_id });
                this.userInfo = body_data.chat_user_info || { USER_ID: user_id };
                if (!this.userInfo.USER_ID) this.userInfo.USER_ID = user_id;
                this.userDialog = true;
            } catch (err) {
                console.error(err);
                this.ShowSnackbar('유저 정보를 불러오는데 실패했습니다.', 'error');
            }
        },

        OpenBanDialog(user_id) {
            this.banTargetUserId = user_id;
            this.banHours = 24;
            this.banDialog = true;
        },

        async BanUser() {
            if (!confirm(`${this.banTargetUserId} 유저를 ${this.banHours}시간 채팅 정지하시겠습니까?`)) return;
            try {
                await this.CARRYGOLD_BAN_CHAT_USER({ user_id: this.banTargetUserId, ban_hours: this.banHours });
                this.ShowSnackbar('채팅 정지 처리되었습니다.', 'success');
                this.banDialog = false;
                this.userDialog = false;
                this.LoadList();
            } catch (err) {
                console.error(err);
                this.ShowSnackbar('정지 처리 중 오류가 발생했습니다.', 'error');
            }
        },

        async UnbanUser(user_id) {
            if (!confirm(`${user_id} 유저의 채팅 정지를 해제하시겠습니까?`)) return;
            try {
                await this.CARRYGOLD_UNBAN_CHAT_USER({ user_id });
                this.ShowSnackbar('정지가 해제되었습니다.', 'success');
                this.userDialog = false;
                this.LoadList();
            } catch (err) {
                console.error(err);
                this.ShowSnackbar('정지 해제 중 오류가 발생했습니다.', 'error');
            }
        },

        IsBanned(info) {
            if (!info || !info.BAN_TIME) return false;
            return new Date(info.BAN_TIME) > new Date();
        },

        GetBanColor(item) {
            if (item.IS_BAN_CHAT === 'Y') return 'red';
            if (item.BAN_TIME && new Date(item.BAN_TIME) > new Date()) return 'orange';
            return 'green';
        },

        GetBanText(item) {
            if (item.IS_BAN_CHAT === 'Y') return '정지';
            if (item.BAN_TIME && new Date(item.BAN_TIME) > new Date()) return '정지중';
            return '정상';
        },

        ParseColorMessage(msg) {
            if (!msg) return '';
            const escaped = msg.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            return escaped.replace(/&lt;color=(#[0-9a-fA-F]{6,8})&gt;([\s\S]*?)&lt;\/color&gt;/g,
                '<span style="color:$1">$2</span>');
        },

        FormatDate(dateStr) {
            if (!dateStr) return '';
            return moment(dateStr).format('YYYY-MM-DD HH:mm');
        },

        ShowSnackbar(text, color) {
            this.snackbarText = text;
            this.snackbarColor = color || 'success';
            this.snackbar = true;
        },
    },

    watch: {
        '$store.state.selected_game_package': function () {
            this.LoadList();
        },
    },
};
</script>
